/**
 * features/hr/PayRun.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the monthly pay run (Phase 1 + R2).
 *
 * The bursar's end-of-month tool. Pick a month, see every payable
 * staff member assembled (gross / PAYE / deductions / net) with a
 * payroll-cost total, then work in bulk OR per person:
 *
 *   Bulk (operate on the TICKED rows):
 *     - Generate selected   → render + upload + write hrPayslips
 *     - Email selected      → sendPayslipEmail for ticked, saved rows
 *   Per row:
 *     - View      → open the stored PDF (or a live preview if not saved)
 *     - Download  → save the PDF locally
 *     - Generate  → render + save just this one
 *     - Email     → email just this one
 *
 * The tick-box per row is how the bursar EXCLUDES someone from the
 * bulk email/generate (untick them). Select-all is in the header.
 *
 * Regular monthly run only — gratuity (final) payslips are produced
 * per-staff on the Payslips tab, since they need contract context.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  Banknote, Play, Mail, Loader2, CheckCircle2, AlertTriangle, Clock,
  Eye, Download, Send, Lock,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { deleteField, doc, onSnapshot } from 'firebase/firestore';
import { storage, functions, db, setDoc } from '@/lib/firebase';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import CollectionCheckModal from '@/features/hr/CollectionCheckModal';
import { setReloadHold } from '@/lib/swUpdate';
import PayslipSendMonitor, { type SendItem, type SendState } from '@/features/hr/PayslipSendMonitor';
import {
  buildCollections, allTicked, type CollectionTick, type Collection, type RunSlip,
} from '@/lib/collectionCheck';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { usePayrollPeriods } from '@/features/hr/usePayrollPeriods';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildPayslipPdf } from '@/lib/payslipPdf';
import {
  assemblePayslip, payslipId, monthPeriod, periodLabel,
  type AssembledPayslip,
} from '@/lib/assemblePayslip';
import {
  fmtPula, round2, MONTH_LABELS, reviewLoans, loanConcernTotals, type PayslipSendStatus,
} from '@/lib/hrPayroll';
import type { LeaveRequestLike } from '@/lib/hrLeave';

interface Row {
  staffId: string;
  employeeNo: string;
  name: string;
  email?: string;
  assembled: AssembledPayslip;
  saved: boolean;
  pdfUrl?: string;
}

/** Ticks for one month's collections: hrPayrollCollectionChecks/{YYYY-MM}. */
const COLLECTION_CHECKS = 'hrPayrollCollectionChecks';

export default function PayRun({ embedded = false }: { embedded?: boolean }) {
  const data = useHrData();
  const { policies: leavePolicies } = useLeavePolicies();
  const { currentUser, userData } = useAuth();
  const actor = { id: currentUser?.uid ?? 'unknown', email: currentUser?.email };
  const disclaimer = data.payrollSettings?.payslipDisclaimer;

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [emailing, setEmailing] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  // The collection check that gates emailing, and the send monitor.
  const [checkTargets, setCheckTargets] = useState<Row[] | null>(null);
  const [ticks, setTicks] = useState<Record<string, CollectionTick | undefined>>({});
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [sendItems, setSendItems] = useState<SendItem[]>([]);
  const [sendStatus, setSendStatus] = useState<Record<string, SendState>>({});
  const [sendReasons, setSendReasons] = useState<Record<string, string>>({});
  const [sendAt, setSendAt] = useState<Record<string, string>>({});

  const period = useMemo(() => monthPeriod(year, month), [year, month]);
  const periodKey = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => onSnapshot(
    doc(db, COLLECTION_CHECKS, periodKey),
    (snap) => setTicks(((snap.data() as { checks?: Record<string, CollectionTick> } | undefined)?.checks) ?? {}),
    () => setTicks({}),
  ), [periodKey]);
  const { getState: getPeriodState } = usePayrollPeriods();
  const lockState = getPeriodState(year, month);
  const savedById = useMemo(
    () => new Map(data.payslips.map((p) => [p.id, p])),
    [data.payslips],
  );

  const rows: Row[] = useMemo(() => {
    if (data.loading) return [];
    return data.staff
      .map((s) => {
        const profile = data.payrollProfiles.find((p) => p.staffId === s.id);
        if (!profile || (profile.basicSalary ?? 0) <= 0) return null;
        const deductions = data.deductions.filter((d) => d.staffId === s.id);
        const leave = data.leave.filter((l) => l.staffId === s.id) as LeaveRequestLike[];
        const allocation = data.leaveAllocations.find((a) => a.staffId === s.id);
        const reference = `SLIP/${year}-${String(month).padStart(2, '0')}/${s.employeeNo}`;
        const assembled = assemblePayslip({
          staff: s, profile, deductions, taxTable: data.activeTaxTable,
          leaveAllocation: allocation, leaveRequests: leave, period, reference,
          // Same leave source as the leave module — without the policies the
          // payslip resolves entitlements from the staff allocation alone.
          leavePolicies, leaveAccrualStart: data.payrollSettings?.leaveAccrualStart,
          sickBalancesFromMonth: data.payrollSettings?.sickBalancesFromMonth,
          // Match PayslipsTab so the pay-run figures and the per-staff tab
          // agree (the omission made the same staff-month compute differently).
        });
        const saved = savedById.get(payslipId(s.id, period));
        return {
          staffId: s.id,
          employeeNo: s.employeeNo,
          name: assembled.staffName,
          email: s.email,
          assembled,
          saved: !!saved,
          pdfUrl: saved?.pdfUrl,
        } as Row;
      })
      .filter((r): r is Row => !!r)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, period, year, month, savedById]);

  const totals = useMemo(() => {
    const sum = (sel: (r: Row) => number) => round2(rows.reduce((s, r) => s + sel(r), 0));
    return {
      gross: sum((r) => r.assembled.earningsTotal),
      paye: sum((r) => r.assembled.paye),
      deductions: sum((r) => r.assembled.deductionsTotal),
      net: sum((r) => r.assembled.net),
    };
  }, [rows]);

  /* The cut-off days matter and are passed: without them the review judges a
     start date the run itself will not use, and reports the wrong month for
     anything set up late. The repayment is judged AS CONFIGURED, so a loan
     starting next month still shows a wrong instalment rather than hiding it
     behind "starts next month". */
  const loanReview = useMemo(
    () => reviewLoans(data.deductions, period, {
      nameOf: (id) => {
        const st = data.staff.find((x) => x.id === id);
        return st ? `${st.firstName} ${st.lastName}`.trim() : undefined;
      },
    }),
    [data.deductions, data.staff, data.payrollSettings, period],
  );
  const loanTotals = useMemo(() => loanConcernTotals(loanReview), [loanReview]);

  /* WHAT IS CHECKED IS WHAT IS SENT. While the month is open, email()
     regenerates each slip from the live figures just before sending it, so
     the live figures are what go out and what the totals are built from.
     Once the month is locked the stored slips are sent as they are, so the
     stored lines are used. Last month is always the stored, finalised run. */
  const collections: Collection[] = useMemo(() => {
    const thisRun: RunSlip[] = rows.map((r) => {
      const stored = savedById.get(payslipId(r.staffId, period));
      return { staffId: r.staffId, name: r.name, lines: lockState.locked && stored ? stored.lines : r.assembled.lines };
    });
    const py = month === 1 ? year - 1 : year;
    const pm = month === 1 ? 12 : month - 1;
    const lastRun: RunSlip[] = data.payslips
      .filter((p) => p.period?.year === py && p.period?.month === pm && p.status === 'finalised')
      .map((p) => ({ staffId: p.staffId, name: p.staffName, lines: p.lines ?? [] }));
    return buildCollections(thisRun, lastRun);
  }, [rows, savedById, period, lockState.locked, data.payslips, year, month]);

  async function setTick(c: Collection, on: boolean) {
    const tick: CollectionTick = {
      total: c.now,
      by: actor.id,
      byName: userData?.displayName || currentUser?.email || 'unknown',
      at: new Date().toISOString(),
    };
    await setDoc(
      doc(db, COLLECTION_CHECKS, periodKey),
      { checks: { [c.key]: on ? tick : deleteField() }, updatedAt: tick.at },
      { merge: true },
    );
    await recordHrAudit({
      action: on ? 'payrun.collection.checked' : 'payrun.collection.unchecked',
      actor,
      subjectId: periodKey,
      after: { collection: c.key, label: c.label, total: c.now, lastMonth: c.last },
    });
  }

  /** Every email of a payslip goes through the check first. */
  function requestEmail(targets: Row[]) {
    if (targets.length === 0) return;
    setCheckTargets(targets);
  }

  const selected = rows.filter((r) => !excluded.has(r.staffId));
  const selectedEmailable = selected.filter((r) => r.saved && r.email);
  const savedCount = rows.filter((r) => r.saved).length;
  const anyExcluded = rows.some((r) => excluded.has(r.staffId));
  const busy = !!progress || emailing || !!rowBusy;

  function toggle(id: string) {
    setExcluded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    setExcluded((prev) => {
      const n = new Set(prev);
      if (anyExcluded) rows.forEach((r) => n.delete(r.staffId));   // select all
      else rows.forEach((r) => n.add(r.staffId));                  // clear all
      return n;
    });
  }

  /** Render + upload + persist one row's payslip. Throws on failure. */
  async function persistOne(r: Row, logo: string | null, nowIso: string) {
    const id = payslipId(r.staffId, period);
    const pdf = buildPayslipPdf(r.assembled, { logoDataUrl: logo, disclaimer });
    const blob = pdf.output('blob');
    const path = `payslips/${r.staffId}/${id}.pdf`;
    const sref = ref(storage, path);
    await uploadBytes(sref, blob, { contentType: 'application/pdf' });
    const url = await getDownloadURL(sref);
    await hr.payslips.set(id, {
      ...r.assembled,
      status: 'finalised',
      pdfUrl: url,
      storagePath: path,
      finalisedAt: nowIso,
      finalisedBy: actor.id,
      // hr.payslips.set overwrites the whole doc. Carry the last email
      // result across, or every regenerate forgets who received theirs.
      send: savedById.get(id)?.send,
    });
  }

  async function generate(targets: Row[]) {
    if (targets.length === 0) return;
    if (lockState.locked) {
      setSaveModal({
        show: true,
        status: 'error',
        message: `${periodLabel(period)} is closed and locked. An admin must reopen it (Payroll → Periods) before generating payslips.`,
      });
      return;
    }
    setProgress({ done: 0, total: targets.length });
    setSaveModal({ show: true, status: 'saving', message: `Generating ${targets.length} payslip${targets.length > 1 ? 's' : ''}…` });
    try {
      const logo = await loadLogoDataUrl();
      const nowIso = new Date().toISOString();
      let done = 0;
      for (const r of targets) {
        await persistOne(r, logo, nowIso);
        done += 1;
        setProgress({ done, total: targets.length });
        setSaveModal({
          show: true,
          status: 'saving',
          message: `Generating payslips… ${done} of ${targets.length}`,
        });
      }
      await recordHrAudit({
        action: targets.length === 1 ? 'payslip.generate' : 'payrun.generate',
        actor,
        subjectId: `${year}-${String(month).padStart(2, '0')}`,
        after: { period: periodLabel(period), count: targets.length },
      });
      setSaveModal({ show: true, status: 'success', message: `Generated ${targets.length} payslip${targets.length > 1 ? 's' : ''} for ${periodLabel(period)}.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setProgress(null);
    }
  }

  /**
   * Send, one person at a time, recording each result for the monitor.
   * Only ever reached through the collection check (requestEmail) or a
   * retry of failures from the monitor, by which point the check passed.
   * `retry` keeps everyone else's result on screen.
   */
  async function email(targets: Row[], retry = false) {
    const items = targets.filter((r) => r.saved);
    const list = items.filter((r) => r.email);
    if (items.length === 0) return;
    const mark = (ids: string[], s: SendState) =>
      setSendStatus((prev) => { const n = { ...prev }; ids.forEach((id) => { n[id] = s; }); return n; });
    if (!retry) {
      setSendItems(items.map((r) => ({ staffId: r.staffId, name: r.name, email: r.email })));
      setSendStatus({});
      setSendReasons({});
      setSendAt({});
    }
    mark(items.filter((r) => !r.email).map((r) => r.staffId), 'noemail');
    mark(list.map((r) => r.staffId), 'queued');
    setMonitorOpen(true);
    if (list.length === 0) return;
    setEmailing(true);
    setReloadHold('payslip-email', true); // no update reload mid-send
    const fn = httpsCallable(functions, 'sendPayslipEmail');
    // sendPayslipEmail attaches the STORED PDF from Storage. To guarantee the
    // emailed slip matches what the system currently shows, regenerate each
    // stored PDF from the live figures immediately before sending. Skip when
    // the period is locked — those figures are the official frozen record.
    const regenerate = !lockState.locked;
    let logo: string | null = null;
    if (regenerate) { try { logo = await loadLogoDataUrl(); } catch { logo = null; } }
    const nowIso = new Date().toISOString();
    let sent = 0, failed = 0;
    for (const r of list) {
      mark([r.staffId], 'sending');
      let reason = '';
      try {
        if (regenerate) await persistOne(r, logo, nowIso); // refresh stored PDF from current figures
        const res = (await fn({
          payslipId: payslipId(r.staffId, period),
          toEmail: r.email,
          staffName: r.name,
          period: periodLabel(period),
        })) as { data?: { ok?: boolean; reason?: string } };
        if (!res?.data?.ok) reason = res?.data?.reason || 'The mail server did not accept it.';
      } catch (e) {
        reason = (e as Error).message || 'The send did not complete.';
      }
      if (reason) {
        failed += 1;
        setSendReasons((prev) => ({ ...prev, [r.staffId]: reason }));
        mark([r.staffId], 'failed');
      } else {
        sent += 1;
        setSendAt((prev) => ({ ...prev, [r.staffId]: new Date().toISOString() }));
        mark([r.staffId], 'sent');
      }
    }
    await recordHrAudit({
      action: 'payrun.email', actor, subjectId: periodKey,
      after: { sent, failed, noEmail: items.length - list.length, regenerated: regenerate, retry },
    });
    setReloadHold('payslip-email', false);
    setEmailing(false);
  }

  /** Retry from the monitor. The check already passed — unless a total has
   *  moved since, in which case it is asked for again. */
  function retryFailed(ids: string[]) {
    const targets = rows.filter((r) => ids.includes(r.staffId));
    if (collections.length > 0 && !allTicked(collections, ticks)) {
      setMonitorOpen(false);
      setCheckTargets(targets);
      return;
    }
    void email(targets, true);
  }

  async function generateRow(r: Row) {
    setRowBusy(r.staffId);
    try { await generate([r]); } finally { setRowBusy(null); }
  }
  function emailRow(r: Row) {
    requestEmail([r]);
  }
  async function viewRow(r: Row) {
    if (r.saved && r.pdfUrl) { window.open(r.pdfUrl, '_blank'); return; }
    setRowBusy(r.staffId);
    try {
      const logo = await loadLogoDataUrl();
      const pdf = buildPayslipPdf(r.assembled, { logoDataUrl: logo, disclaimer });
      window.open(URL.createObjectURL(pdf.output('blob')), '_blank');
    } finally { setRowBusy(null); }
  }
  async function downloadRow(r: Row) {
    setRowBusy(r.staffId);
    try {
      const logo = await loadLogoDataUrl();
      const pdf = buildPayslipPdf(r.assembled, { logoDataUrl: logo, disclaimer });
      pdf.save(`payslip_${r.employeeNo}_${year}-${String(month).padStart(2, '0')}.pdf`);
    } finally { setRowBusy(null); }
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-500 dark:text-gray-400">{periodLabel(period)} · {rows.length} payable staff</p>
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
              {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
          </div>
        </div>
      ) : (
        <GradientHero
          icon={Banknote}
          title="Pay Run"
          subtitle={`${periodLabel(period)} · ${rows.length} payable staff`}
          actions={
            <div className="flex items-center gap-2">
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
                {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
            </div>
          }
        />
      )}

      {lockState.locked && (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
            <Lock size={16} /> {periodLabel(period)} is closed and locked — payslips can't be generated or changed. {lockState.reason === 'auto-closed' ? 'It closed automatically at month-end.' : 'It was closed by an admin.'} An admin can reopen it under Payroll → Periods.
          </div>
        </SoftCard>
      )}

      {!data.activeTaxTable && (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} /> No active tax table — PAYE will be 0 on every slip. An admin should activate the FY2026 table first (Tax Tables).
          </div>
        </SoftCard>
      )}

      {/* LOANS THAT WILL NOT REPAY THEMSELVES — read before the run.
          In August 2026 ten members of staff quietly stopped having a loan
          deducted and nobody noticed for six weeks: a missing repayment
          amount makes a zero instalment, a zero line is dropped from the
          payslip, and nothing anywhere says so. A finalised payslip is hard
          to take back; this is cheap to read first. */}
      {loanReview.length > 0 && (
        <SoftCard hover={false}>
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {loanTotals.needingAction > 0
                  ? `${loanTotals.needingAction} loan${loanTotals.needingAction === 1 ? '' : 's'} will not repay ${loanTotals.needingAction === 1 ? 'itself' : 'themselves'}`
                  : 'Loans worth noting before this run'}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                {loanTotals.collectingNothing > 0
                  ? `${loanTotals.collectingNothing} of them collect nothing at all, leaving ${fmtPula(loanTotals.balanceAtRisk)} outstanding with no arrangement to recover it. Worth fixing before you generate — a finalised payslip has to be undone by hand.`
                  : 'Nothing is uncollectable, but these are worth a look before you generate.'}
              </p>
              <div className="mt-3 space-y-1.5">
                {loanReview.map((c) => (
                  <div
                    key={c.deductionId}
                    className={`rounded-lg px-3 py-2 text-xs ${c.severity === 'action'
                      ? 'bg-amber-50 dark:bg-amber-950/30'
                      : 'bg-gray-50 dark:bg-gray-900/40'}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {c.staffName ?? c.staffId}
                        <span className="ml-2 font-normal text-gray-400">{c.label}</span>
                      </span>
                      <span className="tabular-nums text-gray-700 dark:text-gray-200">
                        {fmtPula(c.balance)} outstanding
                        {c.instalment > 0 ? ` · ${fmtPula(c.instalment)} a month` : ' · nothing set'}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mt-0.5">{c.message}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                A loan that has finished is not listed — that is the outcome everybody wanted.
              </p>
            </div>
          </div>
        </SoftCard>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip tone="blue" value={fmtPula(totals.gross)} label="Total gross" />
        <StatChip tone="gold" value={fmtPula(totals.paye)} label="Total PAYE" />
        <StatChip tone="red" value={fmtPula(totals.deductions)} label="Total deductions" />
        <StatChip tone="emerald" value={fmtPula(totals.net)} label="Total net (payroll cost)" />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
              Payslip preview · {selected.length} of {rows.length} selected
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {savedCount > 0 ? `${savedCount} already generated this month — generating again replaces them.` : 'None generated yet for this month.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(emailing || sendItems.length > 0) && (
              <button
                onClick={() => setMonitorOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--accent-link)] hover:underline"
              >
                {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                {emailing ? 'Sending… view progress' : 'Last send results'}
              </button>
            )}
            <button
              onClick={() => requestEmail(selected)}
              disabled={busy || selectedEmailable.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              title={selectedEmailable.length === 0 ? 'Selected rows must be generated and have an email' : ''}
            >
              {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
              Email selected ({selectedEmailable.length})
            </button>
            <AccentButton icon={progress ? Loader2 : Play} disabled={busy || selected.length === 0} onClick={() => generate(selected)}>
              {progress ? `Generating ${progress.done}/${progress.total}…` : `Generate selected (${selected.length})`}
            </AccentButton>
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 px-5 py-6">
            No payable staff. Set a basic salary on each staff member's Payroll tab first.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pl-4 pr-2 w-8">
                    <input type="checkbox" checked={!anyExcluded} onChange={toggleAll} className="rounded" title="Select all" />
                  </th>
                  <th className="py-2 px-4 font-medium">Staff</th>
                  <th className="py-2 px-4 font-medium text-right">Gross</th>
                  <th className="py-2 px-4 font-medium text-right">PAYE</th>
                  <th className="py-2 px-4 font-medium text-right">Deductions</th>
                  <th className="py-2 px-4 font-medium text-right">Net</th>
                  <th className="py-2 px-4 font-medium text-center">Status</th>
                  <th className="py-2 px-4 font-medium">Emailed</th>
                  <th className="py-2 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {rows.map((r) => {
                  const isBusy = rowBusy === r.staffId;
                  const checked = !excluded.has(r.staffId);
                  return (
                    <tr key={r.staffId} className={checked ? '' : 'opacity-50'}>
                      <td className="py-2 pl-4 pr-2">
                        <input type="checkbox" checked={checked} onChange={() => toggle(r.staffId)} className="rounded" />
                      </td>
                      <td className="py-2 px-4 text-gray-800 dark:text-gray-100">
                        {r.name} <span className="text-xs text-gray-400">#{r.employeeNo}</span>
                        {!r.email && <span className="block text-[10px] text-amber-500">no email on file</span>}
                      </td>
                      <td className="py-2 px-4 text-right text-gray-700 dark:text-gray-200">{fmtPula(r.assembled.earningsTotal)}</td>
                      <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.assembled.paye)}</td>
                      <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.assembled.deductionsTotal)}</td>
                      <td className="py-2 px-4 text-right font-semibold text-gray-800 dark:text-gray-100">{fmtPula(r.assembled.net)}</td>
                      <td className="py-2 px-4 text-center">
                        {r.saved ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            <CheckCircle2 size={11} /> Saved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                            <Clock size={11} /> Pending
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4"><EmailedCell live={sendStatus[r.staffId]} liveReason={sendReasons[r.staffId]} stored={savedById.get(payslipId(r.staffId, period))?.send} hasEmail={!!r.email} /></td>
                      <td className="py-2 px-4">
                        <div className="flex items-center justify-end gap-1">
                          {isBusy && <Loader2 size={14} className="animate-spin text-gray-400 mr-1" />}
                          <IconBtn title="View" onClick={() => viewRow(r)} disabled={busy}><Eye size={15} /></IconBtn>
                          <IconBtn title="Download PDF" onClick={() => downloadRow(r)} disabled={busy}><Download size={15} /></IconBtn>
                          <IconBtn title={r.saved ? 'Regenerate' : 'Generate'} onClick={() => generateRow(r)} disabled={busy}><Play size={15} /></IconBtn>
                          <IconBtn
                            title={!r.saved ? 'Generate first' : !r.email ? 'No email on file' : 'Email payslip'}
                            onClick={() => emailRow(r)}
                            disabled={busy || !r.saved || !r.email}
                          >
                            <Send size={15} />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                  <td className="py-2.5 px-4" colSpan={2}>Total · {rows.length} staff</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(totals.gross)}</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(totals.paye)}</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(totals.deductions)}</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(totals.net)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SoftCard>

      <CollectionCheckModal
        open={!!checkTargets}
        onClose={() => setCheckTargets(null)}
        periodLabel={periodLabel(period)}
        collections={collections}
        ticks={ticks}
        onTick={setTick}
        sendCount={(checkTargets ?? []).filter((r) => r.saved && r.email).length}
        noEmail={(checkTargets ?? []).filter((r) => r.saved && !r.email).map((r) => r.name)}
        notGenerated={(checkTargets ?? []).filter((r) => !r.saved).map((r) => r.name)}
        onSend={() => {
          const targets = checkTargets ?? [];
          setCheckTargets(null);
          void email(targets);
        }}
      />

      <PayslipSendMonitor
        open={monitorOpen}
        onClose={() => setMonitorOpen(false)}
        periodLabel={periodLabel(period)}
        items={sendItems}
        status={sendStatus}
        reasons={sendReasons}
        sentAt={sendAt}
        running={emailing}
        onRetry={retryFailed}
      />

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
        autoDismissMs={3500}
      />
    </div>
  );
}

/** This session's result if there is one, else what the last send saved. */
function EmailedCell({
  live, liveReason, stored, hasEmail,
}: {
  live?: SendState;
  liveReason?: string;
  stored?: PayslipSendStatus;
  hasEmail: boolean;
}) {
  const base = 'text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap';
  const when = (iso?: string) => (iso ? new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
  if (live === 'sending' || live === 'queued') return <span className={`${base} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300`}>{live === 'sending' ? 'Sending…' : 'Waiting'}</span>;
  if (live === 'failed') return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`} title={liveReason}>Failed</span>;
  if (live !== 'sent' && stored?.email === 'failed') return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`} title={stored.lastError}>Failed {when(stored.lastAttemptAt)}</span>;
  if (live === 'sent' || stored?.email === 'sent') return <span className={`${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>Sent {when(live === 'sent' ? undefined : stored?.lastAttemptAt)}</span>;
  if (!hasEmail) return <span className={`${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`}>No email</span>;
  return <span className="text-[11px] text-gray-400">Not sent</span>;
}

function IconBtn({
  title, onClick, disabled, children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="p-1.5 rounded-lg text-gray-500 hover:text-[var(--accent-link)] hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-[var(--accent-link-dark,#93c5fd)] disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
